import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { searchProducts } from '../api/search'
import { PRODUCT_KIND, PRODUCT_STATUS } from '../constants/api'
import { QUERY_KEYS } from '../constants/query'
import {
  SEARCH_DEFAULTS,
  SEARCH_UI,
  type SearchSortOptionKey,
} from '../constants/search'
import { toApiMessage } from '../helpers/api-error'
import { mapSearchProductCard, type CatalogCard } from '../helpers/mapCatalog'
import { resolveSearchSort } from '../helpers/search'
import { useDeliveryLocation } from './useDeliveryLocation'
import { storefrontCoordinates } from './useStorefrontCatalog'

type ProductSearchState = {
  products: CatalogCard[]
  total: number
  page: number
  totalPages: number
  loading: boolean
  loadingMore: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => void
}

export function useProductSearch(
  query: string,
  sortKey: SearchSortOptionKey | string = SEARCH_DEFAULTS.sortKey,
): ProductSearchState {
  const normalizedQuery = query.trim()
  const sort = resolveSearchSort(sortKey)
  const { location } = useDeliveryLocation()
  const coords = storefrontCoordinates(location)

  const searchQuery = useInfiniteQuery({
    queryKey: QUERY_KEYS.search(
      normalizedQuery,
      sort.sortBy,
      sort.sortOrder,
      coords?.latitude ?? null,
      coords?.longitude ?? null,
    ),
    enabled: Boolean(normalizedQuery),
    initialPageParam: SEARCH_DEFAULTS.page as number,
    queryFn: ({ pageParam }) =>
      searchProducts({
        q: normalizedQuery,
        status: PRODUCT_STATUS.active,
        kind: PRODUCT_KIND.standard,
        ...coords,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        page: pageParam,
        limit: SEARCH_DEFAULTS.limit,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
  })

  const pages = searchQuery.data?.pages ?? []
  const lastPage = pages[pages.length - 1]
  const products = pages.flatMap((page, pageIndex) => {
    const offset = pages
      .slice(0, pageIndex)
      .reduce((count, item) => count + item.items.length, 0)
    return page.items.map((item, index) =>
      mapSearchProductCard(item, offset + index),
    )
  })
  const error = searchQuery.error
    ? toApiMessage(searchQuery.error, SEARCH_UI.errorFallback)
    : null

  return useMemo(
    () => ({
      products,
      total: lastPage?.total ?? 0,
      page: lastPage?.page ?? SEARCH_DEFAULTS.page,
      totalPages: lastPage?.totalPages ?? 0,
      loading:
        Boolean(normalizedQuery) &&
        searchQuery.isPending &&
        !searchQuery.data &&
        !error,
      loadingMore: searchQuery.isFetchingNextPage,
      error,
      hasMore: Boolean(searchQuery.hasNextPage),
      loadMore: () => {
        if (
          searchQuery.isPending ||
          searchQuery.isFetchingNextPage ||
          !searchQuery.hasNextPage
        ) {
          return
        }
        void searchQuery.fetchNextPage()
      },
    }),
    [
      error,
      lastPage,
      normalizedQuery,
      products,
      searchQuery.data,
      searchQuery.fetchNextPage,
      searchQuery.hasNextPage,
      searchQuery.isFetchingNextPage,
      searchQuery.isPending,
    ],
  )
}
